/* Homepage-matched, image-ready, one-time landing-page reveals. No dependencies.
   Native image loading, hero slideshows, sticky navigation and maps stay intact. */
(() => {
  'use strict';
  if (window.mrLandingRevealMounted) return;
  window.mrLandingRevealMounted = true;

  const pending = 'mr-landing-reveal-pending';
  const entering = 'mr-landing-reveal-enter';
  const cardSelector = '.mr-gc-card, [data-mrj-card], .mrpc-tour-card, .mr-cards__card, a.bg-card, .mr-related-grid > article, .img-gallery-wrapper-ar';
  const photoSelector = '.mr-gc-photo, .card-image img, .feature-image img, ' +
    '.mrpc-tour-card__media img, .mr-cards__image, .newsletter-photo img, ' +
    '.pts-slide.is-active img, .mrpc-benefit-media.is-active img, ' +
    '.bg-card__media > img, .mr-card-image img, .mr-tour-picture img, .img-gallery-wrapper-ar img';
  const selectors = {
    gallery: '.mr-gc-heading, .mr-gc-card',
    portfolio: '.bg-main__top, a.bg-card, .mr-related-header, .mr-related-grid > article, ' +
      '.mr-related-footer, .mr-portfolio-tour-banner, .img-gallery-wrapper-ar',
    journal: '[data-mrj-featured], [data-mrj-card], #MRJournalNewsletter .newsletter-panel',
    tours: '.mrpc-year__header, .mrpc-tour-card, .pts-header, .pts-slider-wrap, ' +
      '.mrpc-experience__header, .mrpc-experience__media, .mrpc-benefit, ' +
      '.mrpc-contact__layout > div, .mr-cards__heading, .mr-cards__card, .mr-cards__footer'
  };
  const seen = new WeakSet();
  const waiting = new Set();
  const tickets = new WeakMap();
  const cleanupTimers = new Map();
  const refreshScopes = new Set();
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let observer;
  let frame = 0;
  let failed = false;
  const canAnimate = () => !failed && !motion.matches &&
    'IntersectionObserver' in window && !window.Shopify?.designMode &&
    window.CSS?.supports('translate', '0 1px') && window.CSS?.supports('scale', '1');

  function showImmediately(element) {
    waiting.delete(element);
    tickets.delete(element);
    observer?.unobserve(element);
    clearTimeout(cleanupTimers.get(element));
    cleanupTimers.delete(element);
    element.classList.remove(pending, entering);
    element.style.removeProperty('--mr-landing-reveal-delay');
  }

  function showAll() {
    observer?.disconnect();
    document.querySelectorAll('.' + pending + ', .' + entering).forEach(showImmediately);
    waiting.forEach(showImmediately);
    cleanupTimers.forEach((_, element) => showImmediately(element));
  }

  function imageReady(img) {
    return new Promise(resolve => {
      let settled = false;
      let timer;
      const done = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        img.removeEventListener('load', loaded);
        img.removeEventListener('error', done);
        resolve();
      };
      const loaded = () => {
        if (typeof img.decode === 'function') img.decode().then(done, done);
        else done();
      };
      // A slow/broken photograph must never leave text or controls hidden.
      timer = setTimeout(done, 1500);
      if (img.complete) { loaded(); return; }
      img.addEventListener('load', loaded, { once: true });
      img.addEventListener('error', done, { once: true });
      // Only prioritize a photograph after its card reaches the viewport.
      img.loading = 'eager';
    });
  }

  function reveal(element, delay) {
    observer?.unobserve(element);
    if (!waiting.has(element)) return;
    if (!canAnimate() || element.matches(':focus-within')) {
      showImmediately(element);
      return;
    }
    const ticket = tickets.get(element);
    const images = Array.from(element.querySelectorAll(photoSelector)).filter(img =>
      img.tagName === 'IMG' && !img.closest('[hidden]') && img.getClientRects().length &&
      (img.currentSrc || img.getAttribute('src') || img.getAttribute('srcset'))
    );
    Promise.all(images.map(imageReady)).then(() => {
      // Ignore stale image callbacks after rapid filter changes re-arm a card.
      if (!waiting.has(element) || tickets.get(element) !== ticket) return;
      if (!element.isConnected || !canAnimate() || element.closest('[hidden]') ||
          element.matches(':focus-within')) {
        showImmediately(element);
        return;
      }
      waiting.delete(element);
      element.style.setProperty('--mr-landing-reveal-delay', delay + 'ms');
      element.classList.remove(pending);
      element.classList.add(entering);
      // Release animation styles after the photo settles, including hover scale.
      cleanupTimers.set(element, setTimeout(() => showImmediately(element), 2700 + delay));
    }).catch(() => showImmediately(element));
  }

  function init(scope) {
    const main = document.querySelector('body.mr-landing-reveal-page #MainContent');
    const selector = selectors[document.body.dataset.mrLandingReveal];
    if (!main || !selector) return;
    if (!canAnimate()) { showAll(); return; }
    try {
      if (!observer) observer = new IntersectionObserver(entries => {
        try {
          const visible = entries.filter(entry => entry.isIntersecting)
            .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top ||
              a.boundingClientRect.left - b.boundingClientRect.left);
          let rowTop = -Infinity;
          let parent = null;
          let step = 0;
          visible.forEach(entry => {
            const element = entry.target;
            if (element.closest('[hidden]')) return;
            if (element.matches(cardSelector) && element.parentElement === parent &&
                Math.abs(entry.boundingClientRect.top - rowTop) < 60) step++;
            else { step = 0; rowTop = entry.boundingClientRect.top; parent = element.parentElement; }
            reveal(element, Math.min(step, 3) * 150);
          });
        } catch (_) {
          failed = true;
          showAll();
        }
      }, { threshold: 0.12, rootMargin: '0px 0px -56px 0px' });

      const candidates = Array.from(scope.querySelectorAll(selector));
      if (scope.matches?.(selector)) candidates.unshift(scope);
      // Read geometry together before adding styles, avoiding read/write thrashing.
      const measurements = candidates.filter(element => main.contains(element))
        .map(element => ({ element, rect: element.getBoundingClientRect() }));
      measurements.forEach(({ element, rect }) => {
        if (!rect.height || !rect.width || element.closest('[hidden]')) {
          if (waiting.has(element)) {
            showImmediately(element);
            seen.delete(element);
          } else if (cleanupTimers.has(element)) showImmediately(element);
          // Hidden filter results are not "seen"; arm them when they become visible.
          return;
        }
        if (seen.has(element)) {
          // A filter, sort or load-more action can move a pending card onto screen.
          if (waiting.has(element) && rect.top < window.innerHeight) showImmediately(element);
          return;
        }
        seen.add(element);
        element.querySelectorAll('img[loading="lazy"]').forEach(img => { img.decoding = 'async'; });
        // Never hide the first viewport, restored scroll position, or focused content.
        if (rect.top < window.innerHeight || element.matches(':focus-within')) return;
        waiting.add(element);
        tickets.set(element, {});
        element.classList.add(pending);
        observer.observe(element);
      });
    } catch (_) {
      failed = true;
      showAll();
    }
  }

  function schedule(scope = document) {
    refreshScopes.add(scope);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      const scopes = Array.from(refreshScopes);
      refreshScopes.clear();
      // Initial filter/render handlers finish before we measure the final layout.
      if (scopes.includes(document)) init(document);
      else scopes.forEach(scope => { if (scope.isConnected) init(scope); });
    });
  }
  const boot = () => schedule(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  // Explicit render hooks avoid a costly, page-wide MutationObserver.
  document.addEventListener('mr:landing:refresh', event => schedule(event.target));
  document.addEventListener('shopify:section:load', event => schedule(event.target));
  document.addEventListener('shopify:section:unload', event => {
    waiting.forEach(element => { if (event.target.contains(element)) showImmediately(element); });
    cleanupTimers.forEach((_, element) => { if (event.target.contains(element)) showImmediately(element); });
  });
  document.addEventListener('focusin', event => {
    const element = event.target.closest?.('.' + pending + ', .' + entering);
    if (element) showImmediately(element);
  });
  document.addEventListener('beforematch', showAll);
  window.addEventListener('beforeprint', showAll);
  window.addEventListener('pageshow', event => { if (event.persisted) showAll(); });
  window.addEventListener('hashchange', showAll);
  if (motion.addEventListener) motion.addEventListener('change', () => {
    if (motion.matches) showAll();
  });
})();
